import _, { Dictionary } from 'lodash';
import moment from 'moment';
import {
  RtbCampaignPlanType,
  CreativeDeliverType,
  DailyBudgetPlan,
  DeliverType,
  CampaignState,
  AdType,
  RtbCampaignBasic,
  RtbCampaignListBasic,
  ViewTrackingType,
  ViewTrackingTrigger
} from 'core/rtbCampaign/RtbCampaign';
import * as SelectOptionsUtils from 'utils/SelectOptionsUtils';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { Currency, AddonFeatureManager, LocaleMeta } from 'core';
import { ADDONFEATURE, perChannelCampaignKey } from 'core/agency/AddonFeature';
import { getDecimalPlaceCount, getPriceValue } from 'helper/CurrencyHelper';
import { Order } from 'core/order/Order';
import {
  DefaultRtbCampaignBasicFormValidator,
  RtbCampaignBasicFormValidator
} from './RtbCampaignBasicFormValidator';
import { L1Object, L1ObjectObjective } from 'core/l1Object/L1Object';
import { DefaultRtbCampaignManager } from 'core/rtbCampaign/RtbCampaignManager';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import i18n from 'i18n';
import {
  LIMITATION_TYPE,
  LimitationInventorySettings
} from 'containers/Limitations/LimitationSetting/limitationConfig/limitationSettingsType';
import { getDefaultLimitationInventorySettings } from 'containers/Limitations/LimitationSetting/limitationConfig/defaultLimitationInventorySettings';
import { OPERATE, OPERATES } from 'enum/Operate';
import {
  ConversionManager,
  DefaultConversionManager
} from 'core/conversion/ConversionManager';
import { LimitationData } from 'core/limitation/Limitation';
import { CampaignAdTypeSetting } from 'core/channelGroup/ChannelGroupSettings';
import { isEpaperChannelGroup } from 'core/channelGroup/EpaperChannelGroup';

export type TrackingConfig = {
  conversionTracking: {
    options?: SelectOptions[];
    conversionTrackingTypeOptions?: SelectOptions[];
  };
  canUseVideoProgressTracking: boolean;
  getThirdPartyTrackingTypeOptions: () => {
    label: string;
    value: ViewTrackingType;
  }[];
  getThirdPartyTriggerTypeOptions: (
    trackingType: ViewTrackingType
  ) => { label: string; value: ViewTrackingTrigger }[];
};

export type LimitationConfig = {
  defaultLimitationToShow?: {
    name: string;
    operate: OPERATES;
  };
  supportAgeGenderLimitation: boolean;
  supportAudienceEstimation: boolean;
  requireIncludeLimitation?: boolean;
  operationSet: {
    need: OPERATES[];
    notNeed?: OPERATES[];
    other?: [];
  };
};

export interface RtbCampaignBasicFormModel {
  readonly actionType: string;
  readonly l1Object: L1Object;
  readonly addonFeatureManager: AddonFeatureManager;
  readonly canUseDailyBudget: boolean;
  readonly priceModelOptions: SelectOptions[];
  readonly optimizeOptions: SelectOptions[];
  readonly creativeDeliverTypeOptions: SelectOptions[];
  readonly outdoorDeliverTypeOptions: SelectOptions[] | undefined;
  readonly campaignBasic: RtbCampaignBasic;
  readonly campaignDeliverTypeOptions: SelectOptions[];
  readonly order: Order;
  readonly orderName: string;
  readonly agencyProfit?: string;
  readonly currency: string;
  readonly budgetMinimum: number;
  readonly canEditBudgetPlan: boolean;
  readonly canEditPriceModel: boolean;
  readonly canEditEnableMonitor: boolean;
  readonly minDate: string;
  readonly maxDate: string;
  readonly defaultPriceModel: RtbCampaignPlanType;
  readonly campaignAdType: string;
  readonly showOptimizeSection: boolean;
  readonly showFrequencyCap: boolean;
  readonly availablePriceModel: RtbCampaignPlanType[];
  readonly formikValue?: any;
  readonly optimizeAddonEnable: boolean;
  readonly onlyBidCap: boolean;
  readonly isPmpType: boolean;
  readonly canEditVideoViewObjective: boolean;
  readonly limitationConfig: LimitationConfig;
  readonly canCreateWithCreative: boolean;
  readonly trackingConfig: TrackingConfig;
  readonly onlyDaypartingOptimize: boolean;
  readonly onlySupportAcceleratedDeliverType: boolean;
  readonly segmentOnlyDaypart: boolean;
  readonly showDeliverType: boolean;
  readonly hideFixOutdoorSpace: boolean;
  init(): Promise<void>;
  getVideoAdViewObjectiveOptions: (
    priceModel: RtbCampaignPlanType
  ) => SelectOptions[] | undefined;
  canEditOptimize: (priceModel: RtbCampaignPlanType) => boolean;
  bidCapNeedAlignOrderPrice(
    priceModel: RtbCampaignPlanType,
    optimize: L2ObjectOptimizationGoal
  ): boolean;
  canEditBidStrategy(
    priceModel: RtbCampaignPlanType,
    optimize: L2ObjectOptimizationGoal
  ): boolean;
  showFrequencyControl(optimizationGoal: L2ObjectOptimizationGoal): boolean;
  getBidPriceFloorData(): any;
  getCurrentPriceModel(): RtbCampaignPlanType;
  setCurrentPriceModel(planType: RtbCampaignPlanType);
  getBidFloor: (optimize: L2ObjectOptimizationGoal) => number;
  getDefaultOptimizeType: (
    planType: RtbCampaignPlanType
  ) => L2ObjectOptimizationGoal | undefined;
  getRemainBudget: (campaignBudget: number) => number;
  changeDailyBudgetOptions: (dailyBudgetType: DailyBudgetPlan) => void;
  getDailyBudgetState: (
    totalBudget: number,
    dailyBudget: number | undefined,
    totalDay: number
  ) => DAILY_BUDGET_STATE;
  getCampaignTotalDay: (startDate: string, endDate: string) => number;
  state: RtbCampaignBasicFormState;
  readonly event: UpdateEventListener<RtbCampaignBasicFormModel>;
  onPriceModelChangeCallback: (
    type: RtbCampaignPlanType,
    campaignBasic: RtbCampaignBasic
  ) => void;
  validate: (campaignBasic: any, order: Order, localeMeta?: LocaleMeta) => any;
  onUnmount: (handler?: number) => void;
  setFormikValue: (value: any) => void;
  getLimitationInventorySetting(
    requiredOperateOfTaTypes: { [key: string]: OPERATES[] },
    campaignBasic: RtbCampaignBasic,
    localeMeta?: LocaleMeta | undefined
  ): LimitationInventorySettings[];
  getDefaultLimitations(): LimitationData;
  setAdTypeSetting(adTypeSetting: CampaignAdTypeSetting): void;
}

export enum DAILY_BUDGET_STATE {
  DEFAULT,
  UNDER_BUDGET,
  OVER_BUDGET,
  MEET_BUDGET
}

export type RtbCampaignBasicFormProps = {
  readonly model: RtbCampaignBasicFormModel;
};

export type RtbCampaignBasicFormState = {
  dailyBudgetType: DailyBudgetPlan;
};

export type RtbCampaignBasicFormModelConstructorParams = [
  any,
  RtbCampaignBasic,
  Order,
  L1Object,
  AddonFeatureManager,
  (type: RtbCampaignPlanType, campaignBasic: RtbCampaignBasic) => void,
  RtbCampaignListBasic[]?,
  ConversionManager?
];
export abstract class DefaultRtbCampaignBasicFormModel implements RtbCampaignBasicFormModel {
  modelDailyBudgetType: DailyBudgetPlan;
  event: FireableUpdateEventListener<RtbCampaignBasicFormModel>;
  currentPriceModel: RtbCampaignPlanType;
  validator: RtbCampaignBasicFormValidator;
  rtbCampaignManager = new DefaultRtbCampaignManager();
  formikValue?: any;
  optimizeAddonEnable: boolean;
  conversionTrackingOptions?: SelectOptions[];
  adTypeSetting: CampaignAdTypeSetting | undefined;

  constructor(
    public actionType: string,
    private defaultCampaign: any,
    public campaignBasic: RtbCampaignBasic,
    public order: Order,
    public l1Object: L1Object,
    public addonFeatureManager: AddonFeatureManager,
    public onPriceModelChangeCallback: (
      type: RtbCampaignPlanType,
      campaignBasic: RtbCampaignBasic
    ) => void,
    private otherCampaignOfL1Object?: RtbCampaignListBasic[],
    private conversionManager: ConversionManager = new DefaultConversionManager()
  ) {
    this.modelDailyBudgetType =
      !campaignBasic.dailyTargetBudget || campaignBasic.dailyTargetBudget < 1
        ? DailyBudgetPlan.SCHEDULE
        : DailyBudgetPlan.DAILY;
    this.event = new FireableUpdateEventListener<RtbCampaignBasicFormModel>();
    this.currentPriceModel = this.campaignBasic.priceModel;
    this.canEditOptimize = this.canEditOptimize.bind(this);
    this.validator = new DefaultRtbCampaignBasicFormValidator(
      actionType,
      this.order,
      this.defaultCampaign,
      this.campaignBasic,
      this.getBidFloor.bind(this),
      this.addonFeatureManager
    );
    this.optimizeAddonEnable = this.addonFeatureManager.isFeatureEnable(
      perChannelCampaignKey(
        this.l1Object.channelGroupId,
        ADDONFEATURE.CAMPAIGN.ENABLE_OPTIMIZED_PRICE
      )
    );
  }

  /**
   * 這個活動是不是投在電子紙看板上。
   *
   * 放在 base 而不是某個 outdoor 子類：電子紙依渠道的 deliveryUnit 會被分派到
   * DynamicOutdoorDeviceFormModel 或 DynamicOutdoorStoreFormModel（見
   * RtbCampaignSetupFlowPageModel 的 default 分支），寫在 base 才不會漏掉一邊。
   */
  protected get isEpaperCampaign(): boolean {
    return isEpaperChannelGroup(this.l1Object.channelGroupId);
  }

  /**
   * 電子紙看板的活動只留 dayparting，其餘投遞策略整段藏起來（ECOPD-5492）。
   *
   * 藏掉的是最佳化目標、出價上限、計費事件、素材／機台投遞方式與頻次控管：電子紙
   * 買的是「四個固定時段的固定版位」，這些參數在投遞端都沒有作用，留著只會讓人以為
   * 調得動。值仍照既有預設邏輯送給後端，格式不變。
   */
  get onlyDaypartingOptimize(): boolean {
    return this.isEpaperCampaign;
  }

  /**
   * 電子紙看板的活動只給選四個固定時段，不給自由勾選「星期 × 小時」。
   *
   * 這是投遞端的硬限制：一台裝置的排程最多引用五個不重複的 playlist，一天就只有
   * 四個六小時的格子。放行自由選取會讓人選出投不出去的組合。
   */
  get segmentOnlyDaypart(): boolean {
    return this.isEpaperCampaign;
  }

  // 電子紙的投遞速率沒有調整空間（換頁節奏由看板決定），藏起來一律送 STANDARD。
  get showDeliverType(): boolean {
    return !this.isEpaperCampaign;
  }

  // 電子紙一個渠道就是一批固定看板，沒有「挑機台」這件事，整個固定機台區塊藏起來。
  get hideFixOutdoorSpace(): boolean {
    return this.isEpaperCampaign;
  }

  get onlySupportAcceleratedDeliverType(): boolean {
    return false;
  }

  get onlyBidCap(): boolean {
    return false;
  }

  get isPmpType(): boolean {
    return false;
  }

  get outdoorDeliverTypeOptions(): SelectOptions[] | undefined {
    return undefined;
  }

  get campaignAdType(): string {
    return AdType.UNKNOW;
  }

  get showOptimizeSection(): boolean {
    return true;
  }

  get showFrequencyCap(): boolean {
    return true;
  }

  get limitationConfig(): LimitationConfig {
    return {
      supportAgeGenderLimitation: true,
      supportAudienceEstimation: true,
      operationSet: {
        need: [OPERATE.INCLUDE, OPERATE.PREFERRED, OPERATE.NONPREFERRED],
        notNeed: [OPERATE.EXCLUDE],
        other: []
      }
    };
  }

  get canCreateWithCreative(): boolean {
    return false;
  }

  get conversionTrackingTypeOptions(): SelectOptions[] | undefined {
    return undefined;
  }

  get canUseVideoProgressTracking(): boolean {
    return false;
  }

  get trackingConfig() {
    return {
      conversionTracking: {
        options: this.conversionTrackingOptions,
        conversionTrackingTypeOptions: this.conversionTrackingTypeOptions
      },
      canUseVideoProgressTracking: this.canUseVideoProgressTracking,
      getThirdPartyTrackingTypeOptions:
        this.getThirdPartyTrackingTypeOptions.bind(this),
      getThirdPartyTriggerTypeOptions:
        this.getThirdPartyTriggerTypeOptions.bind(this)
    };
  }

  async init() {
    if (!this.conversionTrackingOptions) {
      this.conversionTrackingOptions =
        await this.conversionManager.getConversionOptions(
          this.order.advertiserId
        );
    }
  }

  setAdTypeSetting(adTypeSetting: CampaignAdTypeSetting) {
    this.adTypeSetting = adTypeSetting;
  }

  getDefaultLimitations(): LimitationData {
    return {
      include: [],
      exclude: [],
      preferred: [],
      nonPreferred: [],
      other: []
    };
  }

  getThirdPartyTrackingTypeOptions(): {
    label: string;
    value: ViewTrackingType;
  }[] {
    const pixelOption = {
      label: i18n.t('campaign.strategyAndTracking.thirdPartyTrackingTypePixel'),
      value: ViewTrackingType.PIXEL
    };
    const jsOption = {
      label: i18n.t('campaign.strategyAndTracking.thirdPartyTrackingTypeJs'),
      value: ViewTrackingType.JS
    };
    return [pixelOption, jsOption];
  }

  getThirdPartyTriggerTypeOptions(
    _1: ViewTrackingType
  ): { label: string; value: ViewTrackingTrigger }[] {
    const viewableImpressionOption = {
      label: i18n.t('campaign.strategyAndTracking.viewableImpressionTrigger'),
      value: ViewTrackingTrigger.VIEWABLE_IMPRESSION
    };
    const impressionOption = {
      label: i18n.t('campaign.strategyAndTracking.impressionTrigger'),
      value: ViewTrackingTrigger.IMPRESSION
    };
    return [viewableImpressionOption, impressionOption];
  }

  getVideoAdViewObjectiveOptions = (
    _1: RtbCampaignPlanType
  ): SelectOptions[] | undefined => {
    return undefined;
  };

  getBidPriceFloorData() {
    const data = this.order.campaignBidPrice.find((data) => {
      if (data.adTypeId) {
        return data.adTypeId === this.adTypeSetting?.adTypeId;
      }
      return data.type === this.campaignAdType;
    });
    return data ? data.bidFloor : {};
  }

  bidCapNeedAlignOrderPrice(
    priceModel: RtbCampaignPlanType,
    optimize: L2ObjectOptimizationGoal
  ): boolean {
    if (priceModel === RtbCampaignPlanType.FBCPM) {
      return true;
    }

    const optimizeSameAsPriceModel =
      this.rtbCampaignManager.checkOptimizeSameAsPriceModel(
        priceModel,
        optimize
      );
    return !this.optimizeAddonEnable && optimizeSameAsPriceModel;
  }

  canEditBidStrategy(
    priceModel: RtbCampaignPlanType,
    optimize: L2ObjectOptimizationGoal
  ): boolean {
    if (this.onlyBidCap) {
      return false;
    }
    if (this.l1Object.autoOptimise) {
      return false;
    }
    if (this.bidCapNeedAlignOrderPrice(priceModel, optimize)) {
      return false;
    }
    return true;
  }

  setFormikValue(value: any) {
    this.formikValue = value;
  }

  showFrequencyControl = (optimize: L2ObjectOptimizationGoal): boolean => {
    if (!this.l1Object) {
      return false;
    }
    return this.rtbCampaignManager.showFrequencyControl(
      this.l1Object,
      optimize
    );
  };

  getRtbOptionsMap(): {
    [objective: string]: Dictionary<L2ObjectOptimizationGoal[] | undefined>;
  } {
    const enableOptimizedPrice = this.addonFeatureManager.isFeatureEnable(
      perChannelCampaignKey(
        this.l1Object.channelGroupId,
        ADDONFEATURE.CAMPAIGN.ENABLE_OPTIMIZED_PRICE
      )
    );
    const optionsMap = {
      [L1ObjectObjective.AWARENESS]: {
        [RtbCampaignPlanType.RS]: [
          L2ObjectOptimizationGoal.REACH,
          L2ObjectOptimizationGoal.IMPRESSIONS
        ],
        [RtbCampaignPlanType.FCPM]: [
          L2ObjectOptimizationGoal.REACH,
          L2ObjectOptimizationGoal.IMPRESSIONS
        ],
        [RtbCampaignPlanType.DCPM]: [
          L2ObjectOptimizationGoal.REACH,
          L2ObjectOptimizationGoal.IMPRESSIONS
        ]
      },
      [L1ObjectObjective.TRAFFIC]: _.omitBy(
        {
          [RtbCampaignPlanType.RS]: [L2ObjectOptimizationGoal.CLICKS],
          [RtbCampaignPlanType.FCPM]: enableOptimizedPrice
            ? [L2ObjectOptimizationGoal.CLICKS]
            : undefined,
          [RtbCampaignPlanType.FCPC]: [L2ObjectOptimizationGoal.CLICKS],
          [RtbCampaignPlanType.DCPM]: [L2ObjectOptimizationGoal.CLICKS]
        },
        _.isUndefined
      ),
      [L1ObjectObjective.SALES]: {
        [RtbCampaignPlanType.RS]: [L2ObjectOptimizationGoal.SALES],
        [RtbCampaignPlanType.FCPC]: [L2ObjectOptimizationGoal.SALES]
      },
      [L1ObjectObjective.UNSPECIFIED]: {
        [RtbCampaignPlanType.RS]: [
          L2ObjectOptimizationGoal.REACH,
          L2ObjectOptimizationGoal.CLICKS,
          L2ObjectOptimizationGoal.IMPRESSIONS
        ],
        [RtbCampaignPlanType.FCPC]: [
          L2ObjectOptimizationGoal.REACH,
          L2ObjectOptimizationGoal.CLICKS,
          L2ObjectOptimizationGoal.IMPRESSIONS
        ],
        [RtbCampaignPlanType.FCPM]: [
          L2ObjectOptimizationGoal.REACH,
          L2ObjectOptimizationGoal.CLICKS,
          L2ObjectOptimizationGoal.IMPRESSIONS
        ],
        [RtbCampaignPlanType.DCPM]: [
          L2ObjectOptimizationGoal.REACH,
          L2ObjectOptimizationGoal.CLICKS,
          L2ObjectOptimizationGoal.IMPRESSIONS
        ],
        rb: [
          L2ObjectOptimizationGoal.REACH,
          L2ObjectOptimizationGoal.CLICKS,
          L2ObjectOptimizationGoal.IMPRESSIONS
        ]
      }
    };
    return this.filterOptionsMapByPricingMode(
      optionsMap,
      this.l1Object.channelGroupId
    );
  }

  // 只保留 add-on 已啟用對應計價模式（pricing mode）的 RtbCampaignPlanType；
  // 非計價模式的 key（如 'rb'）沒有對應 add-on，原樣保留。
  protected filterOptionsMapByPricingMode(
    optionsMap: {
      [objective: string]: Dictionary<L2ObjectOptimizationGoal[] | undefined>;
    },
    channelGroupId: number
  ): {
    [objective: string]: Dictionary<L2ObjectOptimizationGoal[] | undefined>;
  } {
    return _.mapValues(optionsMap, (planTypeMap) =>
      _.pickBy(planTypeMap, (_optimizes, planType) => {
        const addon = this.getAddonByPlanTypeOrOptimize(
          planType as RtbCampaignPlanType
        );
        if (!addon) {
          return true;
        }
        return this.addonFeatureManager.isFeatureEnable(
          perChannelCampaignKey(channelGroupId, addon)
        );
      })
    );
  }

  getLimitationInventorySetting(
    requiredOperateOfTaTypes: { [key: string]: string[] },
    campaignBasic: RtbCampaignBasic,
    localeMeta?: LocaleMeta | undefined
  ): LimitationInventorySettings[] {
    const inventorySettings = getDefaultLimitationInventorySettings(
      requiredOperateOfTaTypes,
      campaignBasic.advertiserId,
      LIMITATION_TYPE.CAMPAIGN,
      localeMeta,
      this.campaignAdType,
      this.l1Object.channel
    );
    return inventorySettings;
  }

  getPriceModels() {
    const objective = this.l1Object.objective;
    const optionsMap = this.getRtbOptionsMap();
    return Object.keys(optionsMap[objective]);
  }

  getOptimizes(priceModel: RtbCampaignPlanType) {
    const objective = this.l1Object.objective;
    const optionsMap = this.getRtbOptionsMap();
    const optimizes = optionsMap[objective][priceModel];
    return optimizes ? optimizes : [];
  }

  getCurrentPriceModel(): RtbCampaignPlanType {
    return this.currentPriceModel;
  }

  setCurrentPriceModel(planType: RtbCampaignPlanType) {
    this.currentPriceModel = planType;
  }

  getAddonByPlanTypeOrOptimize(
    planType: RtbCampaignPlanType | L2ObjectOptimizationGoal
  ) {
    switch (planType) {
      case RtbCampaignPlanType.FCPC:
      case L2ObjectOptimizationGoal.CLICKS:
        return ADDONFEATURE.CAMPAIGN.FIXED_CPC;
      case RtbCampaignPlanType.FCPM:
      case L2ObjectOptimizationGoal.IMPRESSIONS:
        return ADDONFEATURE.CAMPAIGN.FIXED_CPM;
      case RtbCampaignPlanType.FCPV:
      case L2ObjectOptimizationGoal.VIDEO_VIEWS:
        return ADDONFEATURE.CAMPAIGN.FIXED_CPV;
      case RtbCampaignPlanType.FVCPM:
        // case L2ObjectOptimizationGoal.VIDEO_VIEWS:
        return ADDONFEATURE.CAMPAIGN.FIXED_VCPM;
      case RtbCampaignPlanType.DCPM:
        return ADDONFEATURE.CAMPAIGN.DYNAMIC_CPM;
      case RtbCampaignPlanType.RS:
        return ADDONFEATURE.CAMPAIGN.REVENUE_SHARING;
      case RtbCampaignPlanType.FACPM:
      case L2ObjectOptimizationGoal.ACPM:
        return ADDONFEATURE.CAMPAIGN.FIXED_ACPM;
      case RtbCampaignPlanType.FWCPM:
      case L2ObjectOptimizationGoal.WCPM:
        return ADDONFEATURE.CAMPAIGN.FIXED_WCPM;
      default:
        return;
    }
  }

  get availablePriceModel() {
    const availablePriceModel: any[] = [];
    this.getPriceModels().forEach((priceModel) => {
      const relatedAddon = this.getAddonByPlanTypeOrOptimize(
        priceModel as RtbCampaignPlanType
      );
      const addonEnable = relatedAddon
        ? this.addonFeatureManager.isFeatureEnable(
            perChannelCampaignKey(this.l1Object.channelGroupId, relatedAddon)
          )
        : true;
      if (addonEnable) {
        availablePriceModel.push(priceModel);
      }
    });
    return availablePriceModel;
  }

  get defaultPriceModel(): RtbCampaignPlanType {
    return this.availablePriceModel.includes(RtbCampaignPlanType.RS)
      ? RtbCampaignPlanType.RS
      : this.availablePriceModel[0];
  }

  get canEditVideoViewObjective(): boolean {
    return this.addonFeatureManager.isFeatureEnable(
      ADDONFEATURE.CAMPAIGN.VIDEO_AD_VIEW_OBJECTIVE
    );
  }

  get priceModelOptions(): SelectOptions[] {
    const options = SelectOptionsUtils.createSelectOptionsFromEnum(
      RtbCampaignPlanType,
      'campaign.labels.'
    );
    return SelectOptionsUtils.filter(options, this.availablePriceModel);
  }

  get optimizeOptions(): SelectOptions[] {
    const validOptimizes = this.getOptimizes(this.currentPriceModel);
    return validOptimizes.map((goal) => ({
      label: i18n.t(`optimizationGoal.${goal.toLowerCase()}`),
      value: goal
    }));
  }

  get campaignDeliverTypeOptions(): SelectOptions[] {
    return SelectOptionsUtils.createSelectOptionsFromEnum(
      DeliverType,
      'campaign.labels.'
    );
  }

  get creativeDeliverTypeOptions(): SelectOptions[] {
    return SelectOptionsUtils.createSelectOptionsFromEnum(
      CreativeDeliverType,
      'campaign.labels.creativeDeliverType.'
    );
  }

  getRemainBudget(campaignBudget: number): number {
    const budgetBalance = _.get(this.l1Object, 'budgetBalance', 0);
    const totalBudget =
      this.defaultCampaign.basic.isDraft ||
      this.defaultCampaign.basic.id === undefined
        ? budgetBalance
        : budgetBalance + this.defaultCampaign.basic.budget;
    return _.round(Number(totalBudget) - campaignBudget, 2);
  }

  get budgetMinimum(): number {
    const budgetMinimum = _.get(
      this.order,
      'campaignConstraint.budgetMinimum',
      100
    );
    const spents = this.campaignBasic.spents ? this.campaignBasic.spents : 0;
    return this.campaignBasic.state === CampaignState.DEACTIVATE
      ? getPriceValue(this.order.currency, spents)
      : Number(budgetMinimum);
  }

  getDailyBudgetState(
    totalBudget: number,
    dailyBudget: number | undefined,
    totalDay: number
  ): DAILY_BUDGET_STATE {
    if (!dailyBudget || dailyBudget < 1 || dailyBudget > totalBudget) {
      return DAILY_BUDGET_STATE.DEFAULT;
    }
    const lastDay = totalBudget / dailyBudget;
    if (lastDay === totalDay) {
      return DAILY_BUDGET_STATE.MEET_BUDGET;
    }
    if (lastDay < totalDay) {
      return DAILY_BUDGET_STATE.OVER_BUDGET;
    }
    return DAILY_BUDGET_STATE.UNDER_BUDGET;
  }

  get canUseDailyBudget(): boolean {
    return (
      // 電子紙的預算只能全走期平均分配：四個固定時段的曝光是排程排出來的，
      // 每日指定預算沒有東西可以壓（ECOPD-5492）。藏起來後 dailyTargetBudget
      // 維持 null，摘要頁本來就會顯示「全走期平均分配」。
      !this.isEpaperCampaign &&
      !!this.l1Object &&
      !this.l1Object.autoOptimise &&
      this.addonFeatureManager.isFeatureEnable(
        ADDONFEATURE.CAMPAIGN.BUDGET_DOMINATE
      )
    );
  }

  canEditOptimize(priceModel: RtbCampaignPlanType): boolean {
    if (
      this.l1Object &&
      this.l1Object.autoOptimise &&
      this.otherCampaignOfL1Object &&
      this.otherCampaignOfL1Object.length > 0
    ) {
      return false;
    }
    return (
      priceModel === RtbCampaignPlanType.RS ||
      priceModel === RtbCampaignPlanType.DCPM ||
      priceModel === RtbCampaignPlanType.FCPM ||
      priceModel === RtbCampaignPlanType.FWCPM ||
      priceModel === RtbCampaignPlanType.FACPM ||
      priceModel === RtbCampaignPlanType.FCPV ||
      this.addonFeatureManager.isFeatureEnable(
        perChannelCampaignKey(
          this.l1Object.channelGroupId,
          ADDONFEATURE.CAMPAIGN.ENABLE_OPTIMIZED_PRICE
        )
      )
    );
  }

  get canEditEnableMonitor(): boolean {
    return this.addonFeatureManager.isFeatureEnable(
      ADDONFEATURE.MONITOR.MONITOR_CAMPAIGN_BEHIND_PROGRESS
    );
  }

  get orderName(): string {
    return `${_.get(this.order, 'projectName')} (${_.get(
      this.order,
      'orderNumber'
    )})`;
  }

  get agencyProfit(): string | undefined {
    const agencyProfit = _.get(this.order, 'orderMargin', 0);
    return _.round(Number(agencyProfit) * 100, 2) + ' %';
  }

  get minDate(): string {
    const orderStartDate = moment(_.get(this.order, 'startDate'));
    const defaultStartDate = moment().isAfter(orderStartDate)
      ? moment().startOf('hour').format('YYYY-MM-DD_HH:mm:ss')
      : orderStartDate.format('YYYY-MM-DD_HH:mm:ss');
    if (this.campaignBasic && this.campaignBasic.startDate) {
      return moment(this.campaignBasic.startDate).isBefore()
        ? moment(this.campaignBasic.startDate).format('YYYY-MM-DD_HH:mm:ss')
        : defaultStartDate;
    }

    return defaultStartDate;
  }

  get maxDate(): string {
    return moment(_.get(this.order, 'endDate'))
      .endOf('day')
      .format('YYYY-MM-DD_HH:mm:ss');
  }

  getDefaultOptimizeType(priceModel: RtbCampaignPlanType) {
    const validOptimizes = this.getOptimizes(priceModel);
    let l1ObjectDefaultOptimizationGoal = validOptimizes[0];
    if (
      this.l1Object &&
      this.l1Object.autoOptimise &&
      this.otherCampaignOfL1Object &&
      this.otherCampaignOfL1Object.length > 0
    ) {
      l1ObjectDefaultOptimizationGoal =
        this.otherCampaignOfL1Object[0].optimize;
    }
    return l1ObjectDefaultOptimizationGoal;
  }

  getCampaignTotalDay(startDate: string, endDate: string): number {
    const format = 'YYYY-MM-DD';
    return moment(endDate, format)
      .add(1, 'days')
      .diff(moment(startDate, format), 'days');
  }

  getBidFloor = (
    optimize: L2ObjectOptimizationGoal | RtbCampaignPlanType
  ): number => {
    const bidFloorData = this.getBidPriceFloorData();
    let optimizeKey = '';
    switch (optimize) {
      case L2ObjectOptimizationGoal.OFFSITE_CONVERSIONS:
      case L2ObjectOptimizationGoal.LINK_CLICKS:
      case L2ObjectOptimizationGoal.CLICKS:
      case L2ObjectOptimizationGoal.SALES:
      case RtbCampaignPlanType.FCPC:
        optimizeKey = 'cpc';
        break;
      case L2ObjectOptimizationGoal.REACH:
      case L2ObjectOptimizationGoal.IMPRESSIONS:
      case RtbCampaignPlanType.FCPM:
        optimizeKey = 'cpm';
        break;
      case RtbCampaignPlanType.FVCPM:
        optimizeKey = 'vcpm';
        break;
      case L2ObjectOptimizationGoal.VIDEO_VIEWS:
      case RtbCampaignPlanType.FCPV:
        optimizeKey = 'cpv';
        break;
      case L2ObjectOptimizationGoal.ACPM:
      case RtbCampaignPlanType.FACPM:
        optimizeKey = 'acpm';
        break;
      case L2ObjectOptimizationGoal.WCPM:
      case RtbCampaignPlanType.FWCPM:
        optimizeKey = 'wcpm';
        break;
      case L2ObjectOptimizationGoal.BCPM:
      case RtbCampaignPlanType.FBCPM:
        optimizeKey = 'bcpm';
        break;
      default:
        optimizeKey = '';
    }
    return _.get(bidFloorData, optimizeKey, 0);
  };

  get currency(): string {
    const currency = _.get(this.order, 'currency', Currency.NTD);
    return currency;
  }

  abstract get canEditBudgetPlan(): boolean;
  abstract get canEditPriceModel(): boolean;

  changeDailyBudgetOptions(dailyBudgetType: DailyBudgetPlan): void {
    this.updateState(dailyBudgetType);
  }

  validate(
    campaignBasic: RtbCampaignBasic,
    order: Order,
    localeMeta?: LocaleMeta
  ) {
    // 預算分配欄位藏起來時（電子紙）不驗每日預算：欄位不顯示，使用者沒有辦法修正，
    // 驗下去只會變成一個看不見又送不出去的錯誤。
    const isDailySchedule =
      this.canUseDailyBudget &&
      this.state.dailyBudgetType.toString() ===
        DailyBudgetPlan.DAILY.toString();
    const decimalPlaceCount = getDecimalPlaceCount(this.currency);
    const otherCampaignCost = this.otherCampaignOfL1Object
      ? this.otherCampaignOfL1Object.reduce((acc, campaign) => {
          return (
            acc +
            Math.max(
              _.ceil(campaign.expectedSpent, decimalPlaceCount),
              this.rtbCampaignManager.getMinBudgetOfCampaign(
                campaign,
                order.campaignConstraint.budgetMinimum
              )
            )
          );
        }, 0)
      : 0;
    return this.validator.validate(
      this.l1Object,
      campaignBasic,
      order,
      isDailySchedule,
      otherCampaignCost,
      this.priceModelOptions,
      this.optimizeOptions,
      localeMeta
    );
  }

  onUnmount(handler?: number) {
    handler && this.event.remove(handler);
    this.modelDailyBudgetType = DailyBudgetPlan.SCHEDULE;
  }

  updateState(dailyBudgetType: DailyBudgetPlan): void {
    this.modelDailyBudgetType = dailyBudgetType;
    this.event.fireEvent(this);
  }

  get state(): RtbCampaignBasicFormState {
    return {
      dailyBudgetType: this.modelDailyBudgetType
    };
  }
}
